import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Check, Trash2, Mail, MailOpen, Download, Inbox as InboxIcon, Users } from "lucide-react";
import { btnGhost, btnDanger, formatErr, formatDate } from "@/components/admin/ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CopyEmailButton = ({ email, testid }) => {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(email);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            toast.error("Copy failed — select the address manually.");
        }
    };
    return (
        <button onClick={copy} data-testid={testid} className={btnGhost}>
            {copied ? <Check size={13} /> : <Copy size={13} />}
            {copied ? "Copied" : "Copy Email"}
        </button>
    );
};

const DeleteButton = ({ onDelete, testidPrefix, label = "Delete" }) => {
    const [confirming, setConfirming] = useState(false);
    if (!confirming) {
        return (
            <button onClick={() => setConfirming(true)} data-testid={`${testidPrefix}-delete`} className={btnDanger}>
                <Trash2 size={13} /> {label}
            </button>
        );
    }
    return (
        <>
            <button onClick={onDelete} data-testid={`${testidPrefix}-confirm-delete`} className={btnDanger}>
                <Trash2 size={13} /> Yes, delete
            </button>
            <button onClick={() => setConfirming(false)} data-testid={`${testidPrefix}-cancel-delete`} className={btnGhost}>
                Keep
            </button>
        </>
    );
};

const InquiryCard = ({ inquiry, onRead, onDelete }) => (
    <div
        data-testid={`inbox-inquiry-${inquiry.id}`}
        className={`rounded-2xl border bg-zinc-900/60 backdrop-blur-md p-5 ${
            inquiry.read ? "border-white/10" : "border-amber-500/50 shadow-[0_0_24px_rgba(200,151,43,0.12)]"
        }`}
    >
        <div className="flex flex-wrap items-center gap-3">
            {!inquiry.read && (
                <span
                    data-testid={`inbox-unread-badge-${inquiry.id}`}
                    className="font-meta text-[10px] tracking-[0.25em] uppercase bg-amber-500 text-black font-bold rounded-full px-2.5 py-1"
                >
                    New
                </span>
            )}
            <h3 className="font-display text-base font-bold text-white">{inquiry.name}</h3>
            <span className="font-meta text-[11px] text-amber-300">{inquiry.email}</span>
            <span className="font-meta text-[10px] tracking-[0.2em] uppercase text-zinc-500 ml-auto">
                {formatDate(inquiry.created_at)}
            </span>
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 font-meta text-[11px] text-zinc-400">
            <span>{inquiry.event_type}</span>
            {inquiry.date && <span>Date: {inquiry.date}</span>}
            {inquiry.location && <span>Location: {inquiry.location}</span>}
            {inquiry.budget && <span>Budget: {inquiry.budget}</span>}
        </div>
        <p className="text-sm text-zinc-300 mt-3 leading-relaxed whitespace-pre-line">{inquiry.message}</p>
        <div className="flex flex-wrap gap-2 mt-4">
            {!inquiry.read && (
                <button onClick={onRead} data-testid={`inbox-mark-read-${inquiry.id}`} className={btnGhost}>
                    <MailOpen size={13} /> Mark Read
                </button>
            )}
            <CopyEmailButton email={inquiry.email} testid={`inbox-copy-email-${inquiry.id}`} />
            <DeleteButton onDelete={onDelete} testidPrefix={`inbox-inquiry-${inquiry.id}`} />
        </div>
    </div>
);

export const Inbox = ({ token }) => {
    const [tab, setTab] = useState("inquiries");
    const [inquiries, setInquiries] = useState(null);
    const [subscribers, setSubscribers] = useState(null);
    const headers = { Authorization: `Bearer ${token}` };

    const load = () => {
        axios.get(`${API}/admin/inquiries`, { headers }).then((r) => setInquiries(r.data)).catch(() => setInquiries([]));
        axios.get(`${API}/admin/subscribers`, { headers }).then((r) => setSubscribers(r.data)).catch(() => setSubscribers([]));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const markRead = async (id) => {
        try {
            await axios.patch(`${API}/admin/inquiries/${id}/read`, {}, { headers });
            setInquiries((list) => list.map((i) => (i.id === id ? { ...i, read: true } : i)));
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail, "Could not update."));
        }
    };

    const deleteInquiry = async (id) => {
        try {
            await axios.delete(`${API}/admin/inquiries/${id}`, { headers });
            setInquiries((list) => list.filter((i) => i.id !== id));
            toast.success("Inquiry deleted.");
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail, "Could not delete."));
        }
    };

    const deleteSubscriber = async (id) => {
        try {
            await axios.delete(`${API}/admin/subscribers/${id}`, { headers });
            setSubscribers((list) => list.filter((s) => s.id !== id));
            toast.success("Subscriber removed.");
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail, "Could not delete."));
        }
    };

    const exportCsv = () => {
        const rows = [["email", "signed_up"], ...(subscribers || []).map((s) => [s.email, s.created_at])];
        const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `ayevibemuzik-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Subscriber list exported.");
    };

    const unread = (inquiries || []).filter((i) => !i.read).length;

    const SubTab = ({ id, label, icon, count, testid }) => (
        <button
            onClick={() => setTab(id)}
            data-testid={testid}
            className={`inline-flex items-center gap-2 font-meta text-[11px] tracking-[0.2em] uppercase rounded-full px-4 py-2.5 border transition-all duration-200 ${
                tab === id
                    ? "bg-amber-500 text-black border-transparent font-bold"
                    : "text-zinc-400 border-white/10 hover:text-amber-200 hover:border-amber-500/40"
            }`}
        >
            {icon} {label}
            {count > 0 && <span className="opacity-70">({count})</span>}
        </button>
    );

    return (
        <section data-testid="admin-inbox-tab">
            <div className="flex flex-wrap items-center gap-3">
                <SubTab
                    id="inquiries"
                    label="Inquiries"
                    icon={<InboxIcon size={13} />}
                    count={(inquiries || []).length}
                    testid="inbox-subtab-inquiries"
                />
                <SubTab
                    id="subscribers"
                    label="Subscribers"
                    icon={<Users size={13} />}
                    count={(subscribers || []).length}
                    testid="inbox-subtab-subscribers"
                />
                {tab === "inquiries" && unread > 0 && (
                    <span data-testid="inbox-unread-count" className="font-meta text-[11px] text-amber-300">
                        {unread} unread
                    </span>
                )}
                {tab === "subscribers" && (subscribers || []).length > 0 && (
                    <button onClick={exportCsv} data-testid="inbox-export-subscribers" className={`${btnGhost} ml-auto`}>
                        <Download size={13} /> Export CSV
                    </button>
                )}
            </div>

            {tab === "inquiries" ? (
                <div className="mt-6 space-y-4">
                    {inquiries === null ? (
                        <div className="rounded-2xl border border-amber-500/10 bg-zinc-900/40 h-28 animate-pulse" />
                    ) : inquiries.length === 0 ? (
                        <div data-testid="inbox-inquiries-empty" className="rounded-2xl border border-dashed border-amber-500/25 bg-zinc-900/40 p-10 text-center">
                            <Mail size={28} className="mx-auto text-amber-500/60" />
                            <p className="text-sm text-zinc-400 mt-3">No inquiries yet. Messages from the contact form land here.</p>
                        </div>
                    ) : (
                        inquiries.map((i) => (
                            <InquiryCard
                                key={i.id}
                                inquiry={i}
                                onRead={() => markRead(i.id)}
                                onDelete={() => deleteInquiry(i.id)}
                            />
                        ))
                    )}
                </div>
            ) : (
                <div className="mt-6 space-y-3">
                    {subscribers === null ? (
                        <div className="rounded-2xl border border-amber-500/10 bg-zinc-900/40 h-28 animate-pulse" />
                    ) : subscribers.length === 0 ? (
                        <div data-testid="inbox-subscribers-empty" className="rounded-2xl border border-dashed border-amber-500/25 bg-zinc-900/40 p-10 text-center">
                            <Users size={28} className="mx-auto text-amber-500/60" />
                            <p className="text-sm text-zinc-400 mt-3">No signups yet. Newsletter emails appear here.</p>
                        </div>
                    ) : (
                        subscribers.map((s) => (
                            <div
                                key={s.id}
                                data-testid={`inbox-subscriber-${s.id}`}
                                className="rounded-xl border border-white/10 bg-zinc-900/60 p-4 flex flex-wrap items-center gap-3"
                            >
                                <span className="text-sm text-white">{s.email}</span>
                                <span className="font-meta text-[10px] tracking-[0.2em] uppercase text-zinc-500">
                                    {formatDate(s.created_at)}
                                </span>
                                <div className="flex flex-wrap gap-2 ml-auto">
                                    <CopyEmailButton email={s.email} testid={`inbox-copy-subscriber-${s.id}`} />
                                    <DeleteButton
                                        onDelete={() => deleteSubscriber(s.id)}
                                        testidPrefix={`inbox-subscriber-${s.id}`}
                                        label="Remove"
                                    />
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </section>
    );
};

export default Inbox;
